import { css } from '@emotion/react';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { shouldNotForwardThemeProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import styled from '@emotion/styled';
import FontSizes from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import LetterSpacings from '@polestar/component-warehouse-react/tokens/letter-spacings/LetterSpacings';
import LineHeights from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import React, { forwardRef } from 'react';
import { overflowEllipsis, overflowTextOffset, } from '@polestar/component-warehouse-react/helpers/style';
import { calculateAlignmentOffset } from '@polestar/component-warehouse-react/components/text/Text.helpers';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
const Text = forwardRef(({ variant, automatedAlignment, ...props }, ref) => {
    const offsetCharacters = typeof automatedAlignment === 'string' ? automatedAlignment : props.children;
    if (!!automatedAlignment && typeof offsetCharacters !== 'string') {
        // eslint-disable-next-line no-console
        console.warn('[Text] automated alignment of text is only supported with strings, while it is unclear (when ReactNodes are provided) otherwise if the first character has any outside spacing to account for.');
    }
    const { isLTR } = useDirection();
    return (React.createElement(Container, { ...props, alignmentOffset: !!automatedAlignment &&
            // At RTL there are no capital letters and therefore the adjusment is not needed. Also not for Latin text combined with Arabic or Hebrew texts below it. That is why we only adjust for LTR, because it will provide edge-cases when we adjust for Arabic or Hebrew characters and for now we skip alignment offset outside LTR.
            isLTR
            ? calculateAlignmentOffset(offsetCharacters)
            : undefined, variant: variant, ref: ref }));
});
Text.displayName = 'Text';
export default Text;
export const LargeText = forwardRef((props, ref) => {
    return (React.createElement(Text, { ref: ref, ...props, variant: {
            fontSize: FontSizes.large,
            letterSpacing: LetterSpacings.large,
            lineHeight: LineHeights.large,
        } }));
});
LargeText.displayName = 'LargeText';
const styleToResponsiveStyle = (style) => {
    return typeof style === 'string' || typeof style === 'number'
        ? {
            mobile: style,
        }
        : style;
};
const Container = styled('span', shouldNotForwardThemeProps)(({ theme, variant, noWrap, lineClamp, alignmentOffset, }) => {
    return css(css `
      color: ${theme.text.color};
      overflow-wrap: anywhere;
    `, !!variant &&
        css(!!variant.fontFamily &&
            css `
            font-family: ${variant.fontFamily};
          `, !!variant.fontWeight &&
            css `
            font-weight: ${variant.fontWeight};
          `, !!variant.fontStyle &&
            css `
            font-style: ${variant.fontStyle};
          `, !!variant.fontSize &&
            css `
            ${responsiveStyles({
                fontSize: styleToResponsiveStyle(variant.fontSize),
            })}
          `, !!variant.letterSpacing &&
            css `
            ${responsiveStyles({
                letterSpacing: styleToResponsiveStyle(variant.letterSpacing),
            })}
          `, !!variant.lineHeight &&
            css `
            ${responsiveStyles({
                lineHeight: styleToResponsiveStyle(variant.lineHeight),
            })}
          `), noWrap &&
        css `
        white-space: nowrap;
        ${overflowEllipsis};
      `, !!lineClamp &&
        css `
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: ${lineClamp};
        -webkit-box-orient: vertical;
        ${overflowTextOffset};
      `, alignmentOffset &&
        css `
        margin-inline-start: ${alignmentOffset};
      `);
});
